import React, { Component } from "react";
import { action, computed, observable } from "mobx";
import { observer } from "mobx-react";
import { Badge, Dropdown, Menu, Radio, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { translateKey as t } from "../../locales/translation";
import {
  faCircleExclamation,
  faCircleInfo,
  faCircleMinus,
  faCirclePlus,
  faEye,
  faEyeSlash,
  faGear,
} from "@fortawesome/free-solid-svg-icons";

/**
 * @tutorial controller
 * To correctly use this component, the controller must contain the following properties:
 
  modalOptionsLocalStorageKey: string

  formModalSize: 'small' | 'default' | 'large'
  formFieldsSize: 'small' | 'default' | 'large'
  showNonMandatoryFields: boolean (optional: do not declare this variable to remove the option from the menu)
  showPasswords: boolean (optional: do not declare this variable to remove the option from the menu)
  showFieldsIcon: boolean (optional: do not declare this variable to remove the option from the menu)

  * You can find an example of how to use these variables in the v2 version of the CRUDs (for example, Devices)
 */

@observer
export class ModalOptions extends Component<{ controller: any }> {
  @observable visibility: boolean = false;
  @observable localStorageObj: any = JSON.parse(
    localStorage.getItem(this.props.controller.modalOptionsLocalStorageKey)
  );

  // Checks if the currently selected options are different from the default ones
  @computed private get defaultOptionsHaveBeenChanged() {
    const { localStorageObj } = this;

    if (!localStorageObj) return false;

    if (
      localStorageObj.formModalSize.default !== localStorageObj.formModalSize.currentlySet ||
      localStorageObj.formFieldsSize.default !== localStorageObj.formFieldsSize.currentlySet ||
      (localStorageObj.showNonMandatoryFields &&
        localStorageObj.showNonMandatoryFields.default !== localStorageObj.showNonMandatoryFields.currentlySet) ||
      (localStorageObj.showFieldsIcon &&
        localStorageObj.showFieldsIcon.default !== localStorageObj.showFieldsIcon.currentlySet)
    ) {
      return true;
    } else {
      return false;
    }
  }

  // === HANDLERS ===
  @action private handleModalSize = (e: any) => {
    this.props.controller.formModalSize = e.target.value;
    this.visibility = false;

    this.localStorageObj.formModalSize.currentlySet = e.target.value;
    localStorage.setItem(this.props.controller.modalOptionsLocalStorageKey, JSON.stringify(this.localStorageObj));
  };

  @action private handleFieldsSize = (e: any) => {
    this.props.controller.formFieldsSize = e.target.value;
    this.visibility = false;

    this.localStorageObj.formFieldsSize.currentlySet = e.target.value;
    localStorage.setItem(this.props.controller.modalOptionsLocalStorageKey, JSON.stringify(this.localStorageObj));
  };

  @action private handleMandatoryFieldsVisibility = () => {
    this.props.controller.showNonMandatoryFields = !this.props.controller.showNonMandatoryFields;
    this.visibility = false;

    this.localStorageObj.showNonMandatoryFields.currentlySet = this.props.controller.showNonMandatoryFields;
    localStorage.setItem(this.props.controller.modalOptionsLocalStorageKey, JSON.stringify(this.localStorageObj));
  };

  @action private handleIconsVisibility = () => {
    this.props.controller.showFieldsIcon = !this.props.controller.showFieldsIcon;
    this.visibility = false;

    this.localStorageObj.showFieldsIcon.currentlySet = this.props.controller.showFieldsIcon;
    localStorage.setItem(this.props.controller.modalOptionsLocalStorageKey, JSON.stringify(this.localStorageObj));
  };

  @action private handlePasswordsHiding = () => {
    this.props.controller.showPasswords = !this.props.controller.showPasswords;
    this.visibility = false;
  };

  // === COMPONENTS ===
  private renderFormSizeSection = () => {
    return (
      <Menu.ItemGroup>
        <Menu.Divider style={{ margin: "-10px -7px 0px -7px" }} />
        <li style={{ padding: "5px" }}>
          <strong>{t("lblFormSize", "Form Size")}</strong>
        </li>
        <Menu.Divider style={{ margin: "0px -7px" }} />

        <Radio.Group id="modal-size-group" value={this.props.controller.formModalSize} onChange={this.handleModalSize}>
          <Radio
            style={{ display: "block", padding: "5px" }}
            name="modal-size-group"
            value="small"
            data-e2e="form-modal-options-form-size-small"
          >
            {t("lblSmall", "Small")}
          </Radio>
          <Radio
            style={{ display: "block", padding: "5px" }}
            name="modal-size-group"
            value="default"
            data-e2e="form-modal-options-form-size-default"
          >
            {t("lblMedium", "Medium")}
          </Radio>
          <Radio
            style={{ display: "block", padding: "5px" }}
            name="modal-size-group"
            value="large"
            data-e2e="form-modal-options-form-size-large"
          >
            {t("lblLarge", "Large")}
          </Radio>
        </Radio.Group>
      </Menu.ItemGroup>
    );
  };

  private renderFieldsSizeSection = () => {
    return (
      <Menu.ItemGroup>
        <Menu.Divider style={{ margin: "-5px -7px 0 -7px" }} />
        <li style={{ padding: "5px" }}>
          <strong>{t("lblFieldsSize", "Fields Size")}</strong>
        </li>
        <Menu.Divider style={{ margin: "0px -7px" }} />

        <Radio.Group
          id="fields-size-group"
          value={this.props.controller.formFieldsSize}
          onChange={this.handleFieldsSize}
        >
          <Radio
            style={{ display: "block", padding: "5px" }}
            name="fields-size-group"
            value="small"
            data-e2e="form-modal-options-fields-size-small"
          >
            {t("lblCompact", "Compact")}
          </Radio>
          <Radio
            style={{ display: "block", padding: "5px" }}
            name="fields-size-group"
            value="default"
            data-e2e="form-modal-options-fields-size-default"
          >
            {t("lblDefault", "Default")}
          </Radio>
          <Radio
            style={{ display: "block", padding: "5px" }}
            name="fields-size-group"
            value="large"
            data-e2e="form-modal-options-fields-size-large"
          >
            {t("lblLarge", "Large")}
          </Radio>
        </Radio.Group>
      </Menu.ItemGroup>
    );
  };

  private renderLayoutSection = () => {
    return (
      this.props.controller.showNonMandatoryFields !== undefined && (
        <Menu.ItemGroup>
          <Menu.Divider style={{ margin: "-5px -7px 0 -7px" }} />
          <li style={{ padding: "5px" }}>
            <strong>{t("lblLayout", "Layout")}</strong>
          </li>
          <Menu.Divider style={{ margin: "0px -7px" }} />

          {/* Show only Mandatory Fields / Show all Fields */}
          <Menu.Item key="0" onClick={this.handleMandatoryFieldsVisibility} style={{ padding: "5px" }}>
            <FontAwesomeIcon icon={this.props.controller.showNonMandatoryFields ? faCircleExclamation : faCircleInfo} />
            {this.props.controller.showNonMandatoryFields
              ? t("lblShowOnlyMandatoryFields", "Show only Mandatory Fields")
              : t("lblShowAllFields", "Show all Fields")}
          </Menu.Item>
        </Menu.ItemGroup>
      )
    );
  };

  private renderOtherSettingsSection = () => {
    return (
      <Menu.ItemGroup>
        <Menu.Divider style={{ margin: "-10px -7px 0 -7px" }} />
        <li style={{ padding: "5px" }}>
          <strong>{t("lblOtherSettings", "Other Settings")}</strong>
        </li>
        <Menu.Divider style={{ margin: "0px -7px" }} />

        {/* Show Passwords / Hide Passwords */}
        {this.props.controller.showPasswords !== undefined && (
          <Menu.Item key="1" onClick={this.handlePasswordsHiding} style={{ padding: "5px" }}>
            <FontAwesomeIcon icon={!this.props.controller.showPasswords ? faEye : faEyeSlash} />
            {!this.props.controller.showPasswords
              ? t("lblShowPasswords", "Show Passwords")
              : t("lblHidePasswords", "Hide Passwords")}
          </Menu.Item>
        )}
        {/* Show Icons / Hide Icons */}
        {this.props.controller.showFieldsIcon !== undefined && (
          <Menu.Item key="2" onClick={this.handleIconsVisibility} style={{ padding: "5px" }}>
            <FontAwesomeIcon icon={!this.props.controller.showFieldsIcon ? faCirclePlus : faCircleMinus} />
            {!this.props.controller.showFieldsIcon ? t("lblShowIcons", "Show Icons") : t("lblHideIcons", "Hide Icons")}
          </Menu.Item>
        )}
      </Menu.ItemGroup>
    );
  };

  private renderOptionsDropdown = () => {
    return (
      <Menu>
        {this.renderFormSizeSection()}
        {this.renderFieldsSizeSection()}

        {this.renderLayoutSection()}

        {(this.props.controller.showPasswords !== undefined || this.props.controller.showFieldsIcon !== undefined) &&
          this.renderOtherSettingsSection()}
      </Menu>
    );
  };

  private renderIconButton = () => {
    return (
      <Dropdown
        overlay={this.renderOptionsDropdown()}
        open={this.visibility}
        onOpenChange={(visibility) => (this.visibility = visibility)}
        trigger={["click"]}
        placement="bottom"
      >
        <Badge status={this.defaultOptionsHaveBeenChanged ? "warning" : null} offset={[-10, 7]}>
          <Tooltip
            title={
              this.defaultOptionsHaveBeenChanged
                ? t(
                    "lblTheDefaultOptionsForThisCRUDHaveBeenChanged",
                    "The default options for this CRUD have been changed"
                  )
                : null
            }
            placement="topLeft"
            arrow={{ pointAtCenter: true }}
          >
            <FontAwesomeIcon
              icon={faGear}
              onClick={() => (this.visibility = !this.visibility)}
              className="modal-icon"
              data-e2e="form-modal-options-settings-icon"
            />
          </Tooltip>
        </Badge>
      </Dropdown>
    );
  };

  public render() {
    return this.renderIconButton();
  }
}
